// Familia modelos "clásica" migrada al atlas-backend (generar cara + guardar
// modelo desde el historial de generadas). Flag ADENTRO del helper, patrón de
// siempre:
//   ON  → atlas con JWT (`user_id` del body NO viaja, sale del token)
//   OFF → webhook n8n byte-idéntico (token estático + user_id en el body).
// Compartido por ModeloGuardadoOption y PruebaNuevaInterfaz, que antes
// duplicaban el fetch inline.

import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';

const GENERAR_MODELO_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/generar_modelo';
const GUARDAR_MODELO_GENERADO_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/guardar_modelo_generado';

export interface GenerateModelFaceBody {
  user_id: string;
  nombre: string;
  genero?: string;
  edad?: string;
  tipo_cuerpo?: string;
  tono_piel?: string;
  color_pelo?: string;
  largo_pelo?: string;
  descripcion?: string;
}

// Extrae el `error_message` tipado del atlas (402 sin créditos / 500) para
// mostrarlo tal cual; si el body no es JSON devuelve null y el caller usa su
// mensaje genérico de siempre.
async function atlasErrorMessage(response: Response): Promise<string | null> {
  const txt = await response.text().catch(() => '');
  try {
    const parsed = JSON.parse(txt) as { error_message?: string };
    return parsed?.error_message || null;
  } catch {
    return null;
  }
}

/**
 * Genera la cara clásica del modelo (cobra 1 crédito). Devuelve el base64 de
 * `image`. Errores → throw con mensaje mostrable (mismos textos que usaban
 * los componentes; el atlas agrega el detalle tipado, ej. "Sin imágenes
 * suficientes" en 402).
 */
export async function postGenerateModelFace(
  body: GenerateModelFaceBody,
): Promise<string> {
  let response: Response;
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, ...rest } = body;
    response = await atlasBackendFetch('/v1/models/generate-face', rest);
    if (!response.ok) {
      throw new Error(
        (await atlasErrorMessage(response)) || 'Error al generar el modelo',
      );
    }
  } else {
    response = await fetch(GENERAR_MODELO_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('Error al generar el modelo');
  }
  const data = (await response.json().catch(() => null)) as {
    response?: string;
    image?: string;
  } | null;
  if (data?.response !== 'success' || !data?.image) {
    throw new Error('No se recibió la imagen generada');
  }
  return data.image;
}

/** Guarda como modelo una imagen del historial de generadas (gratis). */
export async function postSaveModelFromGenerated(args: {
  user_id: string;
  imagen_id: string | number;
}): Promise<void> {
  let response: Response;
  if (USE_ATLAS_BACKEND) {
    response = await atlasBackendFetch('/v1/models/save-from-generated', {
      imagen_id: args.imagen_id,
    });
  } else {
    response = await fetch(GUARDAR_MODELO_GENERADO_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(args),
    });
  }
  const data = (await response.json().catch(() => ({}))) as {
    response?: string;
    error_message?: string;
  };
  if (data?.response !== 'success') {
    throw new Error(data?.error_message || 'No se pudo añadir el modelo');
  }
}
